import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

// --- Modèles publics ---

export interface PlausibleAggregate {
  visitors: number;
  visits: number;
  pageviews: number;
  bounceRate: number;
  visitDuration: number;
  viewsPerVisit: number;
}

export interface TimeseriesPoint {
  date: string;
  visitors: number;
  pageviews: number;
}

export interface BreakdownItem {
  /** Valeur de la dimension (chemin, code pays, appareil, source…) */
  label: string;
  visitors: number;
  pageviews?: number;
}

// --- Format brut de la réponse v2 ---

interface V2Row {
  metrics: number[];
  dimensions: string[];
}

interface V2Response {
  results: V2Row[];
  meta?: { time_labels?: string[] };
}

/** Type de plage : preset ("7d", "30d", "month", "6mo", "12mo", "all", "day")
 *  ou plage custom "YYYY-MM-DD,YYYY-MM-DD". */
export type DateRange = string;

@Injectable({ providedIn: 'root' })
export class PlausibleService {
  constructor(private http: HttpClient) {}

  /** Agrégat global sur la période. Métriques dans l'ordre :
   *  visitors[0], visits[1], pageviews[2], bounce_rate[3], visit_duration[4], views_per_visit[5] */
  getStats(dateRange: DateRange = '7d'): Observable<PlausibleAggregate> {
    return this.http
      .get<V2Response>(`/api/plausible/stats?dateRange=${encodeURIComponent(dateRange)}`)
      .pipe(
        map(res => {
          const m = res.results[0]?.metrics ?? [0, 0, 0, 0, 0, 0];
          return {
            visitors: m[0],
            visits: m[1],
            pageviews: m[2],
            bounceRate: m[3],
            visitDuration: m[4],
            viewsPerVisit: m[5],
          };
        }),
      );
  }

  /** Nombre de visiteurs en temps réel (API v1) */
  getRealtime(): Observable<number> {
    return this.http.get<number>('/api/plausible/realtime');
  }

  /** Timeseries visiteurs + pages vues. Métriques : visitors[0], pageviews[1] */
  getTimeseries(
    dateRange: DateRange = '7d',
    interval: 'day' | 'hour' = 'day',
  ): Observable<TimeseriesPoint[]> {
    return this.http
      .get<V2Response>(
        `/api/plausible/timeseries?dateRange=${encodeURIComponent(dateRange)}&interval=${interval}`,
      )
      .pipe(
        map(res =>
          res.results.map(r => ({
            date: r.dimensions[0] ?? '',
            visitors: r.metrics[0],
            pageviews: r.metrics[1],
          })),
        ),
      );
  }

  /** Top pages (visitors[0], pageviews[1]) */
  getPages(dateRange: DateRange = '7d'): Observable<BreakdownItem[]> {
    return this.http
      .get<V2Response>(`/api/plausible/pages?dateRange=${encodeURIComponent(dateRange)}`)
      .pipe(
        map(res =>
          res.results.map(r => ({
            label: r.dimensions[0],
            visitors: r.metrics[0],
            pageviews: r.metrics[1],
          })),
        ),
      );
  }

  /** Pages d'entrée (visitors[0]) */
  getEntryPages(dateRange: DateRange = '7d'): Observable<BreakdownItem[]> {
    return this.http
      .get<V2Response>(`/api/plausible/entry-pages?dateRange=${encodeURIComponent(dateRange)}`)
      .pipe(
        map(res =>
          res.results.map(r => ({ label: r.dimensions[0], visitors: r.metrics[0] })),
        ),
      );
  }

  /** Pages de sortie (visitors[0]) */
  getExitPages(dateRange: DateRange = '7d'): Observable<BreakdownItem[]> {
    return this.http
      .get<V2Response>(`/api/plausible/exit-pages?dateRange=${encodeURIComponent(dateRange)}`)
      .pipe(
        map(res =>
          res.results.map(r => ({ label: r.dimensions[0], visitors: r.metrics[0] })),
        ),
      );
  }

  /** Répartition par pays — code alpha-2 (visitors[0]) */
  getCountries(dateRange: DateRange = '7d'): Observable<BreakdownItem[]> {
    return this.http
      .get<V2Response>(`/api/plausible/countries?dateRange=${encodeURIComponent(dateRange)}`)
      .pipe(
        map(res =>
          res.results.map(r => ({ label: r.dimensions[0], visitors: r.metrics[0] })),
        ),
      );
  }

  /** Répartition par type d'appareil (visitors[0]) */
  getDevices(dateRange: DateRange = '7d'): Observable<BreakdownItem[]> {
    return this.http
      .get<V2Response>(`/api/plausible/devices?dateRange=${encodeURIComponent(dateRange)}`)
      .pipe(
        map(res =>
          res.results.map(r => ({ label: r.dimensions[0], visitors: r.metrics[0] })),
        ),
      );
  }

  /** Sources de trafic (visitors[0]) */
  getSources(dateRange: DateRange = '7d'): Observable<BreakdownItem[]> {
    return this.http
      .get<V2Response>(`/api/plausible/sources?dateRange=${encodeURIComponent(dateRange)}`)
      .pipe(
        map(res =>
          res.results.map(r => ({ label: r.dimensions[0], visitors: r.metrics[0] })),
        ),
      );
  }
}
